import React from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { privateRoutes } from "router/routes"

import { Bike } from "models"
import { Logo } from "components/Elements/Logos/Logo"
import { BikeIcon, CrossIcon, RightArrowIcon, Button, CardWaveIcon } from "components/Elements"
import "./BikeCard.scoped.scss"

import {
	bikeStatusList,
	unknownBikeStatus,
	BIKE_STATUS_IN_SALE,
	BIKE_STATUS_SOLD,
	BIKE_STATUS_STOLEN,
	BIKE_STATUS_DESTROY,
	PRIVATE_GENDER_VALUE,
} from "utils/constants"
import { IS_FNUCI_SYNC } from "utils/constants/bike/status"
import { BikeRegistrationActionType, useBikeRegistrationProcess } from "features/dashboard/hooks"

const SOCIAL_REASON_MAX_LENGTH = 15

const cardVariants: { [key: string]: string } = {
	default: "default",
	notification: "notification",
}

export type BikeCardProps = {
	bike: Bike
	cardVariant?: keyof typeof cardVariants
	className?: string
}

export const BikeCard: React.FC<BikeCardProps> = ({ bike, cardVariant = "default", className }) => {
	const { bikeRegistrationDispatch } = useBikeRegistrationProcess()

	const { t } = useTranslation()
	const navigate = useNavigate()

	const bikeIsSyncWithFnuci = bike.is_sync_with_fnuci === IS_FNUCI_SYNC

	const goToBikePage = (bike: Bike) => {
		// If bike is not sync, we redirect user to register bike page
		if (!bikeIsSyncWithFnuci) {
			bikeRegistrationDispatch({
				type: BikeRegistrationActionType.BIKE_REGISTRATION_STEP,
				bikeId: bike.bike_id,
				bikeInfo: null,
				bike: bike,
			})
		} else {
			bike?.bike_id && navigate(`${privateRoutes.bike.basePath}/${bike?.bike_id}`)
		}
	}

	const isBikeSold = bike.bike_status === BIKE_STATUS_SOLD
	const isBikeInTransit = isBikeSold && bike.bike_in_transit

	const bikeStatus =
		bikeStatusList.find(
			(status) => status.value === (isBikeInTransit ? BIKE_STATUS_IN_SALE : bike.bike_status)
		) ?? unknownBikeStatus

	const isActionNeededOnBike = bike.bike_action_needed

	const truncatedSocialReason = (socialReason: string) => {
		return socialReason.length > SOCIAL_REASON_MAX_LENGTH
			? socialReason.slice(0, SOCIAL_REASON_MAX_LENGTH) + "..."
			: socialReason
	}
	// prettier-ignore
	const displayedBikeName = bike?.operator_data?.bike_name
		? bike.operator_data.bike_name
		: t("dashboard.bike_name_prefix", { ns: "bike" }) + (bike.bike_owner.gender === PRIVATE_GENDER_VALUE
				? bike.bike_owner.first_name
				: truncatedSocialReason(bike.bike_owner.social_reason))

	return (
		<div className={`c-bike-card ${className}`}>
			{!isActionNeededOnBike && bikeIsSyncWithFnuci && (
				<h2 className={`c-bike-card__title px-3 text-${bikeStatus.color}`}>
					{isBikeInTransit ? t("status.in_transit", { ns: "bike" }) : bikeStatus.label}
				</h2>
			)}
			<div className="c-bike-card__header">
				<CardWaveIcon className={`absolute top-0 text-${bikeStatus.color}`} />

				<BikeIcon
					className={`absolute -bottom-2 right-9 ${isBikeSold ? "" : "opacity-60"}`}
					color="var(--white)"
					size="56"
				/>

				{bikeStatus.value === BIKE_STATUS_STOLEN && (
					<div className="absolute text-4xl text-white bottom-0 right-14">?</div>
				)}

				{bikeStatus.value === BIKE_STATUS_IN_SALE && (
					<div className="absolute text-3xl text-white bottom-2 right-14">
						<RightArrowIcon size="25" />
					</div>
				)}

				{bikeStatus.value === BIKE_STATUS_DESTROY && (
					<div className="absolute bottom-2 right-14">
						<CrossIcon color="white" size="20" />
					</div>
				)}

				<div className="pt-3 px-6 text-lg leading-6 text-white relative">
					{bikeIsSyncWithFnuci && (
						<>
							<p className="font-bold">{displayedBikeName}</p>
							<p>{bike?.bike_id}</p>
						</>
					)}
					{!bikeIsSyncWithFnuci && <p className="font-bold pt-3">{bike?.bike_id}</p>}
				</div>
			</div>
			<div className="px-3 pb-4">
				<Logo
					className={`c-bike-card__logo text-${bikeStatus.color}`}
					width="130"
					height="32"
				/>
			</div>

			{cardVariant === "default" && !isActionNeededOnBike && (
				<div className="px-3 pb-2">
					{bikeIsSyncWithFnuci && (
						<Button
							type="button"
							variant={bikeStatus.color}
							onClick={() => goToBikePage(bike)}
							className="px-8 py-1"
							size="small"
						>
							{t("global.see")}
						</Button>
					)}

					{!bikeIsSyncWithFnuci && (
						<Button
							type="button"
							variant={bikeStatus.color}
							onClick={() => goToBikePage(bike)}
							className="mx-auto"
						>
							{t("global.update")}
						</Button>
					)}
				</div>
			)}
		</div>
	)
}
